<template>
    <div class="organisation-info webcrm-entity-info">
        <div class="header-panel">
            <div :class="['image', 'organisation', { 'empty': !value.OrganisationImageFileExtension }]" :style="imageStyle"></div>
            <div class="name-container">
                <div class="name">{{ fullName }}</div>
                <div class="sub-name" v-if="showType">{{ value.OrganisationType }}</div>
            </div>
            <div class="more-options">
                <el-tooltip :content="$t('show_in_{0}', ['webCRM'])">
                    <div class="open-link" @click="open">open_in_new</div>
                </el-tooltip>
                <el-dropdown trigger="click" @command="selectOrganisation" v-if="showOtherOrganisations">
                    <el-tooltip :content="`${$t('other')} ${$t('terms.organisations').toLowerCase()}`" placement="left">
                        <div class="open-link">expand_more</div>
                    </el-tooltip>
                    <el-dropdown-menu slot="dropdown">
                        <el-dropdown-item v-for="organisation in organisations"
                                          :key="organisation.OrganisationId"
                                          :command="organisation"
                                          :disabled="organisation.OrganisationId == value.OrganisationId">
                            {{ organisation.OrganisationName }}
                        </el-dropdown-item>
                    </el-dropdown-menu>
                </el-dropdown>
            </div>
        </div>
        <tag type="danger" icon="bolt" v-if="showAlert">{{ value.OrganisationAlert }}</tag>
        <div class="body-panel">
            <div class="info-item clickable" v-if="address" @click="openAddress">
                <div class="md-icon">navigation</div>
                <div class="item-text show-all">{{ address }}</div>
            </div>
            <div class="info-item clickable" v-if="showWebAddress" @click="openWeb">
                <div class="md-icon">link</div>
                <div class="item-text">{{ webAddress }}</div>
            </div>
            <div class="info-item clickable" v-if="showPhone" @click="openPhone">
                <div class="md-icon">call</div>
                <div class="item-text">{{ value.OrganisationTelephone }}</div>
            </div>
        </div>
    </div>
</template>

<script lang="ts">
    import Vue, { PropType } from 'vue';

    import tag from '@/controls/tag.vue';

    import { Organisation } from '@/utilities/api/organisations';
    import { NavigationProvider } from '@/utilities/navigation';
    import { MetadataProvider } from '@/utilities/fields/metadata';
    import { FieldName } from '@/utilities/fields/names';

    export default Vue.extend({
        name: 'organisation-info',
        components: {
            tag
        },
        props: {
            value: Object as PropType<Organisation>,
            organisations: Array as PropType<Organisation[]>,
        },
        computed: {
            fullName(): string {
                if (MetadataProvider.getFieldByName(FieldName.OrganisationDivisionName) && this.value.OrganisationDivisionName) {
                    return `${this.value.OrganisationName} - ${this.value.OrganisationDivisionName}`;
                }
                return this.value.OrganisationName;
            },
            imageStyle(): { '--image': string } {
                return {
                    '--image': `url(${NavigationProvider.getOrganisationImageUrl(this.value)})`
                }
            },
            address(): string {
                return [
                    MetadataProvider.getFieldByName(FieldName.OrganisationAddress) && this.value.OrganisationAddress,
                    MetadataProvider.getFieldByName(FieldName.OrganisationPostCode) && this.value.OrganisationPostCode,
                    MetadataProvider.getFieldByName(FieldName.OrganisationCity) && this.value.OrganisationCity,
                    MetadataProvider.getFieldByName(FieldName.OrganisationState) && this.value.OrganisationState,
                    MetadataProvider.getFieldByName(FieldName.OrganisationCountry) && this.value.OrganisationCountryData.Name,
                ].filter(a => a).join(', ');
            },
            webAddress(): string {
                return this.value.OrganisationWww?.replace(/^https?:\/\/(www\.)?/i, '').replace(/\/$/, '');
            },
            showOtherOrganisations(): boolean {
                return this.organisations.length > 1;
            },
            showType(): boolean {
                return !!MetadataProvider.getFieldByName(FieldName.OrganisationType) && !!this.value.OrganisationType;
            },
            showAlert(): boolean {
                return !!MetadataProvider.getFieldByName(FieldName.OrganisationAlert) && !!this.value.OrganisationAlert;
            },
            showWebAddress(): boolean {
                return !!MetadataProvider.getFieldByName(FieldName.OrganisationWww) && !!this.value.OrganisationWww;
            },
            showPhone(): boolean {
                return !!MetadataProvider.getFieldByName(FieldName.OrganisationTelephone) && !!this.value.OrganisationTelephone;
            },
        },
        methods: {
            open() {
                NavigationProvider.go(NavigationProvider.getOrganisationUrl(this.value.OrganisationId));
            },
            openAddress() {
                NavigationProvider.go(NavigationProvider.getGoogleMapsUrl(this.address));
            },
            openWeb() {
                let url = this.value.OrganisationWww;
                if (!url.startsWith('http')) url = `http://${url}`;
                NavigationProvider.go(url);
            },
            openPhone() {
                NavigationProvider.go(`tel:${this.value.OrganisationTelephone}`);
            },
            selectOrganisation(organisation: Organisation) {
                this.$emit('input', organisation);
            },
        }
    });

</script>

<style scoped lang="scss">

</style>